/**
 * Errors reported by Rollbar:
 * - Uncaught exceptions
 * - Uncaught rejections
 * - Rollbar.error()
 *
 * Errors not reported by Rollbar:
 * - console.error()
 * - Caughts exceptions or rejections
 */

import * as RollbarClass from 'rollbar'

const token: string = (window as any).rollbarToken // eslint-disable-line @typescript-eslint/no-explicit-any
const environment: string = (window as any).environment // eslint-disable-line @typescript-eslint/no-explicit-any
const userId: string = (window as any).currentUserId // eslint-disable-line @typescript-eslint/no-explicit-any

const levelMap = {
  critical: 'error',
  error: 'error',
  warning: 'warn',
  info: 'info',
  debug: 'debug'
}

type RollbarFallback = Record<keyof typeof levelMap, (...args: unknown[]) => void>

let Rollbar: RollbarClass | RollbarFallback

// Rollbar configuration
if (token) {
  const rb = new RollbarClass({
    accessToken: token,
    captureUncaught: true,
    captureUnhandledRejections: true,
    payload: {
      person: {
        id: userId
      },
      environment: environment
    },
    transmit: environment !== 'development',
    checkIgnore: function(isUncaught: boolean, args: any[], payload: any) {
      // Ignore errors from Chrome extensions
      if (payload && payload.body && payload.body.trace && payload.body.trace.frames) {
        const frames = payload.body.trace.frames;
        for (let i = 0; i < frames.length; i++) {
          const filename = frames[i].filename;
          if (filename && (
            filename.indexOf('chrome-extension://') === 0 ||
            filename.indexOf('moz-extension://') === 0 ||
            filename.indexOf('safari-extension://') === 0 ||
            filename.indexOf('ms-browser-extension://') === 0
          )) {
            console.log('Rollbar: Ignoring extension error:', args[0]);
            return true; // Ignore this error
          }
        }
      }
      
      // Also check the message for extension-related errors
      if (args && args[0] && typeof args[0] === 'string') {
        const message = args[0];
        if (message.indexOf('chrome-extension://') !== -1 ||
            message.indexOf('moz-extension://') !== -1 ||
            message.indexOf('safari-extension://') !== -1 ||
            message.indexOf('ms-browser-extension://') !== -1) {
          console.log('Rollbar: Ignoring extension error:', args[0]);
          return true; // Ignore this error
        }
      }
      
      return false; // Don't ignore other errors
    }
  })
  rb.configure({
    verbose: true
  })
  rb.global({
    itemsPerMinute: 3,
    maxItems: 6
  })
  Rollbar = rb
// If not Rollbar token is available in window, a fallback will used
} else {
  const rb: Partial<RollbarFallback> = {}
  Object.keys(levelMap).forEach(rbKey => {
    rb[rbKey] = console[levelMap[rbKey]]
  })
  Rollbar = rb as RollbarFallback
}

type transformHandler = (
  payload: Record<string, unknown>
) => void
const rollbarTransforms: transformHandler[] = []
if (Rollbar instanceof RollbarClass) {
  Rollbar.configure({
    transform: function (payload) {
      rollbarTransforms.forEach(handler => {
        try {
          handler(payload as Record<string, unknown>)
        } catch (e) {
          console.log(e)
        }
      })
    }
  })
}
/**
 * Adds a new trasnform function to Rollbar payload transforms.
 * This function can alter the payload before seding it to Rollbar.
 *
 * https://docs.rollbar.com/docs/javascript#transforming-the-payload
 */
const RollbarTransform = function (
  handler: transformHandler
): void {
  rollbarTransforms.push(handler)
}

export { Rollbar, RollbarTransform }
